import { Controller } from "@hotwired/stimulus"
import DOMPurify from "dompurify"
import Trix from "trix"
import { ImageSizeHelper } from "~/helpers/image_size_helper";
import { TrixHelper } from "~/helpers/trix_helper";

export default class extends Controller {
  static get targets() {
    return [
      "editor", "attributeButton", "subjectField", "imageAttachmentInput",
      "fileAttachmentInput", "linkButton", "bodyContent"
    ]
  }

  static values = {
    campaignId: Number,
    followUpId: Number,
    messageTemplateId: Number,
    maxAttachmentSize: { type: Number, default: 10 },
    showAttributes: { type: Boolean, default: false },
    lastFocusedElement: { type: String, default: "body" }
  }

  connect() {
    const editor = this.editorTarget.editor
    let editorContent = this.bodyContentTarget.value

    if (this.showAttributesValue) {
      // Create attribute for custom attribute
      Trix.config.textAttributes.customAttr = { tagName: "code" }
      Trix.config.textAttributes.deactivatedCustomAttr = { tagName: "" }
      editorContent = this.highlightAttributes(editorContent)
    } else {
      // Remove custom attribute from global config.
      delete Trix.config.textAttributes.customAttr
    }

    // Update the content of the Editor with or without the custom attribute as tag
    if (this.hasBodyContentTarget) {
      this.editorTarget.value = editorContent
    }

    // Display information about links when the select is on it
    this.editorTarget.addEventListener("trix-selection-change", () => {
      document.querySelector("#trix-link-dialog")?.remove()
      this.linkDialog()
      this.imageDialog()
      this.videoDialog()
    })

    // Validate the attachment
    this.editorTarget.addEventListener("trix-file-accept", (event) => {
      if (event.file.size > this.maxAttachmentSizeValue * 1024 * 1024) { // convert to bytes
        event.preventDefault()
        App.displayFlash("danger", i18n.t("js.trix_format.max_file_size_exceeded", { max_value: this.maxAttachmentSizeValue }))
      }
    })

    // Upload the attachment
    this.editorTarget.addEventListener("trix-attachment-add", (event) => {
      if (event.attachment.getAttributes().type === "video") { return }
      let attachmentComingFromTemplate = !event.attachment.file && event.attachment.getURL()?.match(/^https:\/\//i)

      if (attachmentComingFromTemplate) {
        return;
      }

      if (!event.attachment.file) {
        App.displayFlash("danger", i18n.t("js.trix_format.please_upload_instead"))

        event.attachment.remove()

        return
      }

      this.uploadFileAttachment(event.attachment, event.target.id)
    })

    this.editorTarget.addEventListener("trix-change", () => {
      // On change, re-check content to remove unvalid attributes
      if (this.showAttributesValue) {
        const cursorPosition = editor.getSelectedRange()
        const testRegex = new RegExp(/{{[A-Za-z1-9_]+(:"[^:}]+")?}}/)
        const currentAttribute = Object.keys(editor.composition.currentAttributes)[0]
        const elementRange = editor.getDocument().getRangeOfCommonAttributeAtPosition(currentAttribute, cursorPosition[1])
        const elementText = editor.getDocument().getStringAtRange(elementRange)

        if (currentAttribute === "customAttr" || currentAttribute === "deactivatedCustomAttr") {
          if (testRegex.test(elementText)) {
            // It's valid, lets make sure there is correct attribute
            this._changeElementAttribute(elementRange, cursorPosition, "deactivatedCustomAttr", "customAttr")
          } else {
            // It's invalid, lets make sure there is correct attribute
            this._changeElementAttribute(elementRange, cursorPosition, "customAttr", "deactivatedCustomAttr")
          }
        }
      }
    })

    this.editorTarget.addEventListener("trix-attachment-remove", (event) => {
      const removeEvent = new CustomEvent("trix-editor:attachment-removed", {
        detail: { attachment: event.attachment, editorId: event.target.id }
      })
      window.dispatchEvent(removeEvent)
    })

    // Add the image attachment to the editor
    if (this.hasImageAttachmentInputTarget) {
      this.imageAttachmentInputTarget.addEventListener("change", (event) => {
        this.editorTarget.editor.insertFile(event.target.files[0])
      })
    }

    // Add the file attachment to the editor
    if (this.hasFileAttachmentInputTarget) {
      this.fileAttachmentInputTarget.addEventListener("change", (event) => {
        this.editorTarget.editor.insertFile(event.target.files[0])
      })
    }

    // Make sure the tooltip on the editor's buttons have a slight delay
    App.tooltip(".trix-toolbar-button", { "delay": { show: 500 } })

    App.modalEvent($("#href-modal"), "hidden.bs.modal", () => {
      // Fix for GH-2879: once the href-modal is closed, the compose-modal is
      // still open on page so the modal-open class should still be applied on
      // the body.
      if (document.querySelector(".compose-modal") && document.querySelector(".compose-modal").classList.contains("in")) {
        document.body.classList.add("modal-open")
      }
    })

    if (this.hasAttributeButtonTarget) {
      this.updateFocusElement()
    }

    // Handle copying of URLs with attribute placeholders to stop the transformation into an absolute URL
    this.editorTarget.addEventListener("copy", (event) => {
      const selection = window.getSelection()
      const selectedContent = selection.toString()
      const selectedRange = selection.getRangeAt(0)
      const selectedHtml = selectedRange.cloneContents()
      const container = document.createElement('div')
      container.appendChild(selectedHtml)

      const urlPattern = new RegExp(`href=.*?{{.*?}}`, 'g')
      const hasAttributeLink = urlPattern.test(container.innerHTML)

      if (hasAttributeLink) {
        event.preventDefault()
        event.clipboardData.setData('text/plain', selectedContent)
        event.clipboardData.setData('text/html', container.innerHTML)
      }
    })
  }

  updateFocusElement() {
    var _this = this

    $(_this.subjectFieldTarget).on("focus", function () {
      _this.lastFocusedElementValue = "subject"
      _this.attributeButtonTarget.setAttribute("data-focus", "subject")
    })

    $(_this.editorTarget).on("focus", function () {
      _this.lastFocusedElementValue = "body"
      _this.attributeButtonTarget.setAttribute("data-focus", "body")
    })

    // Handle Turbo frame updates
    _this.element.addEventListener("turbo:frame-load", () => {
      // Re-initialize focus state after Turbo frame updates
      if (document.activeElement === _this.subjectFieldTarget) {
        _this.lastFocusedElementValue = "subject"
        _this.attributeButtonTarget.setAttribute("data-focus", "subject")
      } else if (document.activeElement === _this.editorTarget) {
        _this.lastFocusedElementValue = "body"
        _this.attributeButtonTarget.setAttribute("data-focus", "body")
      }
    })
  }

  toggleFormat(e) {
    var format = e.target.dataset.format
    if (format === undefined) {
      format = e.target.parentElement.dataset.format
    }

    const editor = this.editorTarget.editor
    var currentAttributes = editor.composition.currentAttributes

    if (currentAttributes[format]) {
      editor.deactivateAttribute(format)
    }
    else {
      editor.activateAttribute(format)
    }
  }

  createLinkAction() {
    this.createLink("")
  }

  createLink(currentUrl) {
    const editor = this.editorTarget.editor
    const _this = this
    const modal = $("#href-modal")
    const attributeName = currentUrl.match(/{{(.[^:}]+):?.*/) !== null ? currentUrl.match(/{{(.[^:}]+):?.*/)[1] : false

    if (attributeName) {
      $("[aria-controls='link-form-attributes']", modal).trigger('click')

      $("#link-attribute-identifier").val(attributeName)
      $("#href-field").val("")
    } else {
      $("[aria-controls='link-form-url']", modal).trigger('click')

      $("#href-field").val(currentUrl)
      $("#link-attribute-identifier").val("")
    }

    App.modal(modal, "show")

    // Bind the click handler immediately to avoid timing issues
    $("#href-button").unbind("click")

    $("#href-button").on("click", function () {
      try {
        if ($("#link-form-url").is(":visible")) {
          editor.activateAttribute("href", _this.prependUrlProtocol($("#href-field").val().trim()))

        } else if ($("#link-form-attributes").is(":visible")) {
          const attributeString = `{{${$("#link-attribute-identifier").val()}}}`
          const cursorPosition = editor.getSelectedRange()[1]
          const attributeLength = attributeString.length

          editor.activateAttribute("href", attributeString)
          editor.setSelectedRange([cursorPosition, cursorPosition + attributeLength])
          editor.activateAttribute("customAttr")
        }

        App.enableForm()
        App.modal($("#href-modal"), "hide")
      } catch (error) {
        console.error("Error in href button click handler:", error)
        // Fallback: try to close the modal anyway
        App.modal($("#href-modal"), "hide")
      }
      return false
    })

    // Select the field after a short delay to ensure modal is fully shown
    setTimeout(function () {
      if (!attributeName) {
        $("#href-field").select()
      }
    }, 100)
  }

  prependUrlProtocol(url) {
    // Prepend the "http://" scheme if the URL isn't an attribute and misses
    // a protocol.
    if (!url.startsWith("{{")) {
      try {
        const urlObject = new URL(url)

        if (urlObject.protocol === null) {
          url = "http://" + urlObject.href
        }
      } catch {
        url = "http://" + url
      }
    }

    return url
  }

  verifyThenInsertAttribute(editor, attributeString) {
    // To prevent nested attributes to be used, we ensure that there's
    // as many open brackets before the cursor that after the cursor.
    var cursorPosition = editor.getSelectedRange()[1]
    var textBeforeAttribute = editor.getDocument().getStringAtRange([0, cursorPosition])
    var openingBracketsCount = (textBeforeAttribute.match(/{{/g) || []).length
    var closingBracketsCount = (textBeforeAttribute.match(/}}/g) || []).length
    var attributeLength = attributeString.length

    if (openingBracketsCount === closingBracketsCount) {
      editor.insertString(attributeString)
      editor.setSelectedRange([cursorPosition, cursorPosition + attributeLength])
      editor.activateAttribute("customAttr")
    } else {
      App.displayFlash("danger", "Nested attributes aren't supported")
    }
  }

  selectLink() {
    const editor = this.editorTarget.editor
    let currentAttributes = editor.composition.currentAttributes

    // If the range is not collapsed, we skip this method; we directly
    // take the range from the user.
    const range = editor.getSelectedRange()
    if (range[0] !== range[1]) {
      return
    }

    // Get the start of the link
    while (currentAttributes.href) {
      editor.moveCursorInDirection("backward")
      currentAttributes = editor.composition.currentAttributes
    }

    const start = editor.getSelectedRange()[0]
    editor.moveCursorInDirection("forward")

    currentAttributes = editor.composition.currentAttributes

    // Get the end of the link
    while (currentAttributes.href) {
      currentAttributes = editor.composition.currentAttributes
      editor.moveCursorInDirection("forward")
    }

    var end = editor.getSelectedRange()[1]

    // Select the whole link
    editor.setSelectedRange([start, end])

    $("#trix-link-dialog").remove()
  }

  deleteLink() {
    const editor = this.editorTarget.editor
    var pos = editor.getSelectedRange()
    this.selectLink()
    editor.deactivateAttribute("href")
    editor.setSelectedRange(pos)
  }

  editLink() {
    const editor = this.editorTarget.editor
    var currentAttributes = editor.composition.currentAttributes
    this.selectLink()
    this.createLink(currentAttributes.href)
  }

  linkDialog() {
    var _this = this
    const editor = this.editorTarget.editor
    var currentAttributes = editor.composition.currentAttributes

    if (currentAttributes.href) {
      var cursorLocation = editor.getClientRectAtPosition(editor.getSelectedRange()[0])
      const sanitizedUrl = DOMPurify.sanitize(currentAttributes.href)
      var link = `<a href='${sanitizedUrl}' target='_blank' rel='noopener noreferrer'>${sanitizedUrl}</a>`

      if (!/^((http|https|):\/\/)/.test(currentAttributes.href)) {
        link = sanitizedUrl
      }

      $("body").append(`
        <div id='trix-link-dialog'>
          ${link} |
          <a id='trix-link-dialog-change'>${i18n.t("js.trix_format.edit")}</a> |
          <a id='trix-link-dialog-remove'>${i18n.t("js.trix_format.remove")}</a>
        </div>`)

      $("#trix-link-dialog").show().css({
        position: "absolute",
        marginLeft: 0,
        marginTop: 0,
        top: cursorLocation.y + 25 + window.scrollY,
        left: cursorLocation.x
      })

      $("#trix-link-dialog-change").on("click", function () {
        _this.editLink()
      })

      $("#trix-link-dialog-remove").on("click", function () {
        _this.deleteLink()
      })
    }

    // Deactivate link button when selecting an image
    if (currentAttributes.presentation === "gallery") {
      _this.linkButtonTarget.setAttribute("data-bs-original-title", i18n.t("js.trix_format.link_not_allowed"))
      _this.linkButtonTarget.setAttribute("disabled", true)
    } else {
      _this.linkButtonTarget.setAttribute("data-bs-original-title", i18n.t("js.trix_format.insert_link"))
      _this.linkButtonTarget.removeAttribute("disabled")
    }
  }

  showVideoModal(attachmentId, videoUrl, thumbnailUrl) {
    this.dispatch(
      "videoModalOpen",
      {
        detail: {
          campaignId: this.campaignIdValue,
          followUpId: this.followUpIdValue,
          attachmentId: attachmentId,
          videoUrl: videoUrl,
          thumbnailUrl: thumbnailUrl,
        }
      }
    )
  }

  videoDialog() {
    const editor = this.editorTarget.editor
    let currentSelectedAttachments = editor.composition.getSelectedAttachments();

    if (currentSelectedAttachments.length > 1) { return }
    let currentSelectedAttachment = currentSelectedAttachments[0]
    let type = currentSelectedAttachment?.attributes?.values?.type
    if (type !== 'video') { return }

    if (currentSelectedAttachment) {
      let imgFigure = editor.element.querySelector(`[data-trix-id='${currentSelectedAttachment.id}']`)
      let imgPosition = imgFigure.getBoundingClientRect()

      let node = TrixHelper.renderDialog(imgPosition)
      node.innerHTML = `
        <a id='trix-image-dialog-remove'>${i18n.t("js.trix_format.remove")}</a> |
        <a id='trix-image-dialog-change'>${i18n.t("js.trix_format.edit")}</a>
      `

      node.querySelector("#trix-image-dialog-remove").addEventListener("click", () => {
        editor.composition.removeAttachment(currentSelectedAttachment)
      })
      node.querySelector("#trix-image-dialog-change").addEventListener("click", () => {
        this.showVideoModal(
          currentSelectedAttachment.id,
          currentSelectedAttachment.attributes.values.videoUrl,
          currentSelectedAttachment.attributes.values.thumbnailUrl
        )
        node.remove()
      })

      imgFigure.appendChild(node)
    }
  }

  imageDialog() {
    const editor = this.editorTarget.editor
    let currentSelectedAttachments = editor.composition.getSelectedAttachments();

    if (currentSelectedAttachments.length > 1) { return }
    let currentSelectedAttachment = currentSelectedAttachments[0]
    let type = currentSelectedAttachment?.attributes?.values?.type
    if (type === 'video') { return }

    if (currentSelectedAttachment) {
      let imgElement = editor.element.querySelector(`[data-trix-id='${currentSelectedAttachment.id}'] img`)
      let imgPosition = imgElement.getBoundingClientRect()

      let node = TrixHelper.renderDialog(imgPosition)
      node.innerHTML = `
        <a class='trix-image-dialog-change' data-image-size="small">${i18n.t("js.trix_format.image_sizes.small")}</a> |
        <a class='trix-image-dialog-change' data-image-size="right-fit">${i18n.t("js.trix_format.image_sizes.right_fit")}</a> |
        <a class='trix-image-dialog-change' data-image-size="original">${i18n.t("js.trix_format.image_sizes.original")}</a> |
        <a id='trix-image-dialog-remove'>${i18n.t("js.trix_format.remove")}</a>
      `
      node.querySelectorAll(".trix-image-dialog-change").forEach((el) => {
        el.addEventListener("click", (e) => {
          let imageSize = e.target.dataset.imageSize
          let imgElement = editor.element.querySelector(`[data-trix-id='${currentSelectedAttachment.id}'] img`)
          let dimensions = ImageSizeHelper.calculateImageSize(imgElement, imageSize);

          currentSelectedAttachment.setAttributes({
            width: dimensions.width,
            height: dimensions.height,
          })

          node.remove()
          editor.composition.setSelectedRange(0, 0)
        })
      })
      node.querySelector("#trix-image-dialog-remove").addEventListener("click", () => {
        editor.composition.removeAttachment(currentSelectedAttachment)
      })

      editor.element.querySelector(`[data-trix-id='${currentSelectedAttachment.id}']`).appendChild(node)
    }
  }

  createAttribute(e) {
    const editor = this.editorTarget.editor
    var _this = this

    App.modal($("#attribute-modal"), "show")

    setTimeout(function () {
      $("#attribute-identifier").val("first_name")
      $("#attribute-fallback").val("")

      $("#attribute-form").unbind("submit").on("submit", function () {
        var attributeString = `{{${$("#attribute-identifier").val()}:"${$("#attribute-fallback").val()}"}}`
        let focusTarget = null

        if (document.activeElement === _this.subjectFieldTarget) {
          focusTarget = "subject"
        } else if (document.activeElement === _this.editorTarget) {
          focusTarget = "body"
        }

        if (!focusTarget) {
          focusTarget = _this.lastFocusedElementValue
        }

        if (!focusTarget) {
          focusTarget = e.target.getAttribute("data-focus") || "body"
        }

        if (focusTarget === "subject") {
          _this.subjectFieldTarget.value = `${_this.subjectFieldTarget.value}${attributeString}`
          _this.subjectFieldTarget.dispatchEvent(new Event("keyup"))
        }
        else {
          _this.verifyThenInsertAttribute(editor, attributeString)
        }

        App.enableForm()
        App.modal($("#attribute-modal"), "hide")
        return false
      })
    }, 300)
  }

  addVideo({ detail: { followUpId, thumbnailHtml, videoUrl, thumbnailUrl } }) {
    if (followUpId && this.followUpIdValue && this.followUpIdValue !== followUpId) {
      // There are multiple editors on the page and the video was added to a different editor
      return
    }

    let attachment = new Trix.Attachment({
      type: "video",
      contentType: "image/jpeg",
      content: thumbnailHtml.trim(),
      videoUrl: videoUrl,
      thumbnailUrl: thumbnailUrl
    })
    this.editorTarget.editor.insertAttachment(attachment)
  }

  highlightAttributes(content) {
    String.prototype.replaceAtIndex = function (index, currentValueLength, newValue) {
      return this.substring(0, index) + newValue + this.substring(index + currentValueLength)
    }

    // This regex relies on capture groups instead of matches.
    // The goal is to match first everything we don't want but do not capture it and afterward capture whatever is left.
    const regex = /(?:<code>|href="|href=\\"){{[^}]+}}(?:<\/code>)*|({{[^}]+[^ ]}})/gm
    const matches = content.matchAll(regex)
    let updatedContent = content
    let indexOffset = 0

    for (const match of matches) {
      if (match[1]) {
        let replacedContent = updatedContent.replaceAtIndex(match.index + indexOffset, match[1].length, `<code>${match[1]}</code>`)
        updatedContent = replacedContent
        indexOffset += 13 // <code></code> length
      }
    }

    return updatedContent
  }


  addImageAttachment() {
    $(this.imageAttachmentInputTarget).click()
  }

  addFileAttachment() {
    $(this.fileAttachmentInputTarget).click()
  }

  uploadFileAttachment(attachment, editorId = undefined) {
    App.displayFlash("pending", "Uploading attachment")

    this.uploadFile(attachment.file, setProgress, setAttributes, removeAttachment)

    function setProgress(progress) {
      attachment.setUploadProgress(progress)
    }

    function setAttributes(attributes) {
      attachment.setAttributes(attributes)

      // Emit event to update attachments toolbar
      const fileUploadedEvent = new CustomEvent("file-uploaded", {
        detail: { editorId: editorId }
      })
      window.dispatchEvent(fileUploadedEvent)
      App.displayFlash("success", "Attachment added")
    }

    function removeAttachment(error) {
      if (error !== undefined) {
        App.displayFlash("danger", error)
      }

      attachment.remove()
    }
  }

  uploadFile(file, progressCallback, successCallback, errorCallback) {
    var formData = this.createFormData(file)
    var xhr = new XMLHttpRequest()

    xhr.open("POST", "/message-attachments", true)
    xhr.setRequestHeader("X-CSRF-Token", $("meta[name='csrf-token']").attr("content"))

    xhr.upload.addEventListener("progress", function (event) {
      var progress = event.loaded / event.total * 100
      progressCallback(progress)
    })

    xhr.addEventListener("load", function () {
      var response = JSON.parse(xhr.responseText)

      if (xhr.status === 200) {
        successCallback({ id: response.id, url: response.url, href: response.url })
      } else if (xhr.status === 400) {
        errorCallback(response.error)
      } else {
        errorCallback("The upload failed. Please retry later.")
      }
    })

    xhr.send(formData)
  }

  createFormData(file) {
    var data = new FormData()
    data.append("file", file)

    if (this.campaignIdValue > 0) {
      data.append("campaign_id", this.campaignIdValue)
    }

    if (this.messageTemplateIdValue > 0) {
      data.append("message_template_id", this.messageTemplateIdValue)
    }

    return data
  }

  _changeElementAttribute(elementRange, cursorPosition, from, to) {
    this.editorTarget.editor.setSelectedRange(elementRange)
    this.editorTarget.editor.deactivateAttribute(from)
    this.editorTarget.editor.activateAttribute(to)
    this.editorTarget.editor.setSelectedRange(cursorPosition)
  }
}
